import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productAPI } from '../../services/api';
import ProductList from './ProductList';
import ProductFilters from './ProductFilters';

const ProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 0,
    size: 12
  });
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 12
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, [filters]);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Clean filters (remove empty values)
      const cleanFilters = Object.entries(filters)
        .reduce((acc, [key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {});

      const response = await productAPI.getProducts(cleanFilters);
      
      setProducts(response.data.content || []);
      setPagination({
        page: response.data.number || 0,
        totalPages: response.data.totalPages || 0,
        totalElements: response.data.totalElements || 0,
        size: response.data.size || 12
      });
    } catch (error) {
      console.error('Failed to load products:', error);
      setError('Failed to load products. Please try again.');
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({
      ...newFilters,
      page: 0, // Reset to first page when filters change
      size: filters.size
    });
  };

  const handleProductClick = (product) => {
    navigate(`/products/${product.id}`);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  };

  const headerStyle = {
    marginBottom: '30px',
    textAlign: 'center'
  };

  const contentStyle = {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '30px',
    alignItems: 'start'
  };

  const paginationStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '30px',
    padding: '20px 0'
  };

  const pageButtonStyle = {
    padding: '8px 12px',
    border: '1px solid #dee2e6',
    backgroundColor: '#fff',
    color: '#495057',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s'
  };

  const activePageButtonStyle = {
    ...pageButtonStyle,
    backgroundColor: '#007bff',
    color: 'white',
    borderColor: '#007bff'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={{ color: '#333', marginBottom: '8px' }}>Product Catalog</h1>
        <p style={{ color: '#666', margin: 0 }}>
          Discover our complete range of automotive parts and accessories
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px 16px',
          borderRadius: '4px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Main Content */}
      <div style={contentStyle}>
        {/* Filters Sidebar */}
        <div style={{ position: 'sticky', top: '20px' }}>
          <ProductFilters 
            onFilterChange={handleFilterChange}
            currentFilters={filters}
          />
        </div>

        {/* Products Grid */}
        <div>
          {/* Results Summary */}
          {!isLoading && !error && (
            <div style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#495057'
            }}>
              Showing {pagination.totalElements} product{pagination.totalElements !== 1 ? 's' : ''}
              {pagination.totalPages > 1 && (
                <span> - Page {pagination.page + 1} of {pagination.totalPages}</span>
              )}
            </div>
          )}

          {/* Product List */}
          <ProductList
            products={products}
            isLoading={isLoading}
            onProductClick={handleProductClick}
          />

          {/* Pagination */}
          {!isLoading && !error && pagination.totalPages > 1 && (
            <div style={paginationStyle}>
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 0}
                style={{
                  ...pageButtonStyle,
                  opacity: pagination.page <= 0 ? 0.5 : 1,
                  cursor: pagination.page <= 0 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>

              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i;
                } else if (pagination.page < 3) {
                  pageNum = i;
                } else if (pagination.page >= pagination.totalPages - 3) {
                  pageNum = pagination.totalPages - 5 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === pagination.page 
                        ? activePageButtonStyle 
                        : pageButtonStyle
                    }
                  >
                    {pageNum + 1}
                  </button>
                );
              })}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                style={{
                  ...pageButtonStyle,
                  opacity: pagination.page >= pagination.totalPages - 1 ? 0.5 : 1,
                  cursor: pagination.page >= pagination.totalPages - 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCatalog;